import { createContext, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { CallContactRequestBody, getUserInfoQuery, GetUserInfoResponse, useGetUserInfo } from "../../packages/apiv2-react-query/client/apiv2Components";
import { SET_ALERT } from "../actions";
import { useQueryClient } from "@tanstack/react-query";
import { MeInterface } from "../../_types/user";

interface UserInfoContextValue {
	subscribedVoipProvider: CallContactRequestBody["provider"] | null;
	voipProvider: CallContactRequestBody["provider"] | null;
	isLoading: boolean;
	isError: boolean;
	invalidateUserInfo: () => Promise<void>;
}

const UserInfoContext = createContext<UserInfoContextValue | undefined>(undefined);

export function UserInfoProvider({ me, children }: { me: MeInterface; children: React.ReactNode }) {
	const isAuthenticated = !!me?.token;

	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const {
		data: userInfoData,
		isLoading,
		isError,
	} = useGetUserInfo(
		{
			queryParams: {
				select: ["integrationConfiguration", "customer"],
				expand: ["customer"],
			},
		},
		{ enabled: isAuthenticated }
	);

	const invalidateUserInfo = async () =>
		queryClient.invalidateQueries({
			queryKey: getUserInfoQuery({
				queryParams: {
					select: ["integrationConfiguration", "customer"],
					expand: ["customer"],
				},
			}).queryKey,
		});

	const { enabled: voipProvider, subscribed: subscribedVoipProvider } = determineVoipProvidersStatus(userInfoData?.data?.integrationConfiguration);

	useEffect(() => {
		if (isError) {
			dispatch({
				type: SET_ALERT,
				alert: {
					type: "error",
					message: "Error loading user information.",
				},
			});
		}
	}, [isError, dispatch]);

	return <UserInfoContext.Provider value={{ voipProvider, subscribedVoipProvider, isLoading: isLoading, isError, invalidateUserInfo }}>{children}</UserInfoContext.Provider>;
}
export function useUserInfo() {
	const context = useContext(UserInfoContext);

	if (!context) {
		throw new Error("useUserInfo must be used within a UserInfoProvider");
	}

	return context;
}

export const determineVoipProvidersStatus = (userIntegrationConfigs: GetUserInfoResponse["data"]["integrationConfiguration"]) => {
	if (!userIntegrationConfigs) return { subscribed: null, enabled: null };
	const subscribedProviders: Array<"cloudcall" | "dialpad"> = ["cloudcall", "dialpad"];
	const enabledProviders: CallContactRequestBody["provider"][] = ["cloudcall", "dialpad", "devyce"];
	const subscribed = subscribedProviders.find((p) => userIntegrationConfigs[p]?.subscribed) || null;
	const enabled = enabledProviders.find((p) => userIntegrationConfigs[p]?.enabled) || null;
	return { subscribed, enabled };
};
